"use client";

import { ADD_ONESIGNAL_SUBSCRIPTION_MUTATION } from "@/gql/one-signal";
import { useAuth } from "@/hooks/use-auth";
import { logger } from "@/utils/logger";
import { useMutation } from "@apollo/client";
import Script from "next/script";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const ONESIGNAL_APP_ID = "b32d0c69-01f0-46d5-ad0e-2ff24378ca0b";
const SAFARI_WEB_ID = "web.onesignal.auto.35c3b21f-3634-4ed2-bd52-fd09e2637415";
const PROMPT_DELAY_MS = 20_000; // 20 seconds delay
const TOAST_STORAGE_KEY = "onesignal_toast_dismissed";

interface OneSignalNotifications {
	requestPermission: () => Promise<void>;
	permission: boolean;
}

interface OneSignalSDK {
	init: (config: {
		appId: string;
		safari_web_id: string;
		notifyButton: { enable: boolean };
		allowLocalhostAsSecureOrigin?: boolean;
		serviceWorkerParam?: { scope: string };
		serviceWorkerPath?: string;
	}) => Promise<void>;
	login: (externalId: string) => Promise<void>;
	logout: () => Promise<void>;
	Notifications: OneSignalNotifications;
	User: {
		PushSubscription: {
			id: string | null | undefined;
			optedIn: boolean;
		};
	};
}

declare global {
	// eslint-disable-next-line @typescript-eslint/no-empty-object-type
	interface Window {
		OneSignalDeferred?: Array<(OneSignal: OneSignalSDK) => Promise<void>>;
	}
}

export function OneSignalInit() {
	const [shouldInit, setShouldInit] = useState(false);
	const [sdkLoaded, setSdkLoaded] = useState(false);
	const [addSubscription] = useMutation(ADD_ONESIGNAL_SUBSCRIPTION_MUTATION);
	const { user } = useAuth();

	useEffect(() => {
		// Only run on client
		if (typeof window === "undefined") return;

		// Don't initialize if notifications are not supported
		if (!("Notification" in window)) return;

		// Don't initialize if already granted or denied
		if (Notification.permission !== "default") return;

		// Check if user already dismissed the toast in this session
		if (sessionStorage.getItem(TOAST_STORAGE_KEY)) return;

		// Set up OneSignalDeferred array immediately so it's ready when script loads
		window.OneSignalDeferred = window.OneSignalDeferred || [];

		// Wait before loading the SDK
		const timer = setTimeout(() => {
			setShouldInit(true);
		}, PROMPT_DELAY_MS);

		return () => clearTimeout(timer);
	}, []);

	// Initialize OneSignal when shouldInit becomes true
	useEffect(() => {
		if (!shouldInit) return;

		window.OneSignalDeferred = window.OneSignalDeferred || [];
		window.OneSignalDeferred.push(async function (OneSignal) {
			try {
				await OneSignal.init({
					appId: ONESIGNAL_APP_ID,
					safari_web_id: SAFARI_WEB_ID,
					notifyButton: {
						enable: false, // Disable the built-in bell icon
					},
					allowLocalhostAsSecureOrigin: process.env.NODE_ENV === "development",
					serviceWorkerParam: { scope: "/" },
					serviceWorkerPath: "sw.js",
				});
				setSdkLoaded(true);
			} catch (error) {
				if (!error?.toString().includes("Can only be used on: https://settlin.in")) {
					logger.debug("[OneSignal] Initialization failed (non-critical - app works without notifications):", error);
				}
			}
		});
	}, [shouldInit]);

	// Sync user status with OneSignal
	useEffect(() => {
		if (!sdkLoaded) return;

		window.OneSignalDeferred?.push(async OneSignal => {
			if (user?.id) {
				await OneSignal.login(user.id);
			} else {
				await OneSignal.logout();
			}
		});
	}, [sdkLoaded, user?.id]);

	// Show toast when SDK is loaded
	useEffect(() => {
		if (!sdkLoaded) return;

		// Small delay to ensure OneSignal is fully ready
		const timer = setTimeout(() => {
			toast("🔔 Stay notified!", {
				description: "Get instant notifications about your property visits and updates",
				duration: 15000,
				action: {
					label: "Enable",
					onClick: () => {
						window.OneSignalDeferred?.push(async OneSignal => {
							try {
								await OneSignal.Notifications.requestPermission();
								if (OneSignal.Notifications.permission) {
									const subscriptionId = OneSignal.User.PushSubscription.id;
									if (subscriptionId) {
										await addSubscription({
											variables: {
												params: {
													addSubscriptionId: {
														oneSignalPlayerId: subscriptionId,
														oneSignalAppId: ONESIGNAL_APP_ID,
													},
												},
											},
										});
									}
								}
							} catch {
								// User may have denied or closed the prompt
							}
						});
					},
				},
				onDismiss: () => {
					// Remember that user dismissed the toast for this session
					sessionStorage.setItem(TOAST_STORAGE_KEY, "true");
				},
			});
		}, 500);

		return () => clearTimeout(timer);
	}, [addSubscription, sdkLoaded]);

	if (!shouldInit) return null;

	return (
		<Script
			src="https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js"
			strategy="afterInteractive"
			onError={e => {
				logger.debug("[OneSignal] SDK script failed to load (non-critical):", e);
			}}
		/>
	);
}
